import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { getInventoryDropdown } from '../utils/api';
import { formatModelCode } from '../utils/modelCategoryRanges';
import { roundRupees } from '../utils/money';
import { WHOLESALE_CATEGORY_LABELS } from '../utils/wholesaleOrderForm';
import ModelPicker from './ModelPicker';

const RetailOrderEditPanel = ({ order, open, onClose, onSave, saving = false }) => {
  const [inventory, setInventory] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [advance, setAdvance] = useState(0);
  const [settledPrice, setSettledPrice] = useState('');
  const [colorRequest, setColorRequest] = useState('');
  const [showColorNote, setShowColorNote] = useState(false);

  const currentModelId = order?.items?.[0]?.model
    ? String(order.items[0].model._id || order.items[0].model)
    : '';

  useEffect(() => {
    if (!open || !order) return;

    setCustomerName(order.customerName || '');
    setPhone(order.phone || '');
    setCustomerAddress(order.customerAddress || '');
    setAdvance(roundRupees(order.items?.[0]?.advance || order.totalAdvance || 0));
    setSettledPrice(
      order.settledPrice !== undefined &&
        order.settledPrice !== null &&
        Number.isFinite(Number(order.settledPrice))
        ? roundRupees(Number(order.settledPrice))
        : ''
    );
    const note = order.items?.[0]?.colorRequest || '';
    setColorRequest(note);
    setShowColorNote(Boolean(String(note).trim()));
    setSelectedModel(null);

    let cancelled = false;
    const load = async () => {
      setLoadingInventory(true);
      try {
        const data = await getInventoryDropdown('retail');
        if (cancelled) return;
        const withCurrentAvailable = (data || []).map((inv) => {
          if (String(inv._id) === currentModelId) {
            return { ...inv, isAvailable: true };
          }
          return inv;
        });
        setInventory(withCurrentAvailable);
        const match = withCurrentAvailable.find((inv) => String(inv._id) === currentModelId);
        if (match) setSelectedModel(match);
      } catch (error) {
        if (!cancelled) {
          toast.error(error.message || 'Failed to load inventory');
        }
      } finally {
        if (!cancelled) setLoadingInventory(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [open, order, currentModelId]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('overflow-hidden');
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, saving]);

  const catalogTotal = useMemo(
    () => (selectedModel ? roundRupees(selectedModel.price) : 0),
    [selectedModel]
  );

  const hasSettled =
    settledPrice !== '' &&
    settledPrice !== null &&
    settledPrice !== undefined &&
    Number.isFinite(Number(settledPrice));
  const effectiveTotal = hasSettled ? roundRupees(Number(settledPrice)) : catalogTotal;
  const advanceValue = roundRupees(advance);
  const balance = roundRupees(effectiveTotal - advanceValue);

  const formatMoney = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN')}`;

  const settledError = useMemo(() => {
    if (!hasSettled) return null;
    if (Number(settledPrice) < 0) return 'Agreed price cannot be negative';
    return null;
  }, [hasSettled, settledPrice]);

  const advanceError = useMemo(() => {
    if (advanceValue < 0) return 'Advance cannot be negative';
    if (advanceValue > effectiveTotal) {
      const minAgreed = `₹${Number(advanceValue || 0).toLocaleString('en-IN')}`;
      return hasSettled
        ? `Advance already received exceeds agreed price — set agreed price to at least ${minAgreed} (or adjust advance)`
        : `Advance already received exceeds this price — set agreed price to at least ${minAgreed} (or adjust advance)`;
    }
    return null;
  }, [advanceValue, effectiveTotal, hasSettled]);

  const handleSettledChange = (raw) => {
    if (raw === '') {
      setSettledPrice('');
      return;
    }
    const parsed = parseFloat(raw);
    const next = Number.isNaN(parsed) ? 0 : parsed;
    setSettledPrice(next);
  };

  const handleModelSelect = (inv) => {
    setSelectedModel(inv);
  };

  const handleSave = () => {
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    const trimmedPhone = phone.replace(/\D/g, '').slice(0, 10);
    if (trimmedPhone && !/^[6-9]\d{9}$/.test(trimmedPhone)) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    if (!selectedModel) {
      toast.error('Please select a model');
      return;
    }
    if (advanceError || settledError) return;

    onSave({
      customerName: customerName.trim(),
      phone: trimmedPhone,
      customerAddress: customerAddress.trim(),
      model: selectedModel._id,
      advance: advanceValue,
      settledPrice: hasSettled ? roundRupees(Number(settledPrice)) : '',
      colorRequest: colorRequest.trim(),
    });
  };

  if (!open || !order) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />

      <div
        className="fixed top-0 right-0 h-full max-h-[100dvh] w-full sm:max-w-[95vw] sm:w-[42rem] bg-white shadow-2xl z-50 flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="retail-edit-panel-title"
      >
        <div className="shrink-0 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 flex justify-between items-start">
          <div className="min-w-0 pr-4">
            <h2 id="retail-edit-panel-title" className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
              Edit retail order
            </h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{order.orderNo}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-500 hover:text-gray-700 text-xl p-1 hover:bg-gray-100 rounded shrink-0 disabled:opacity-50"
            aria-label="Close panel"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-1">
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Name *</label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="toolbar-input w-full max-w-full focus:ring-green-500 focus:border-green-500"
                placeholder="Customer name"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                className="toolbar-input w-full max-w-full focus:ring-green-500 focus:border-green-500"
                placeholder="Optional"
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-0.5">Address</label>
              <input
                type="text"
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                className="toolbar-input w-full max-w-full focus:ring-green-500 focus:border-green-500"
                placeholder="Optional"
                disabled={saving}
              />
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Model</h3>
            {loadingInventory ? (
              <p className="text-sm text-gray-500 py-6 text-center">Loading models…</p>
            ) : (
              <div className="h-[14rem] sm:h-[16rem] border border-gray-200 rounded-lg overflow-hidden flex flex-col min-h-0">
                <ModelPicker
                  inventory={inventory}
                  fillHeight
                  compactMobile
                  selectedModelId={selectedModel?._id || ''}
                  onSelect={handleModelSelect}
                  accentRing="ring-green-500"
                />
              </div>
            )}
          </div>

          {selectedModel && (
            <div className="border border-gray-100 rounded-lg p-3 space-y-3 bg-gray-50/50">
              <div className="flex gap-3 min-w-0">
                <div className="w-16 h-16 shrink-0 rounded-md bg-white border border-gray-200 overflow-hidden flex items-center justify-center">
                  {selectedModel.photo ? (
                    <img src={selectedModel.photo} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-[10px] text-gray-400">No photo</span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{selectedModel.modelName}</p>
                  <p className="text-xs text-gray-600">
                    {formatModelCode(selectedModel.size, selectedModel.modelNumber)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {WHOLESALE_CATEGORY_LABELS[selectedModel.size] || selectedModel.size || 'N/A'}
                    {' · '}
                    <span className="font-semibold text-green-700">{formatMoney(selectedModel.price)}</span>
                    {' · Qty 1'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Agreed price <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    value={
                      settledPrice === '' || settledPrice === null || settledPrice === undefined
                        ? ''
                        : settledPrice
                    }
                    onChange={(e) => handleSettledChange(e.target.value)}
                    min="0"
                    step="1"
                    placeholder="Leave empty for original"
                    disabled={saving}
                    className={`toolbar-input h-9 w-36 text-sm text-center px-2 focus:ring-green-500 focus:border-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${settledError ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setSettledPrice('')}
                    disabled={saving}
                    className="admin-btn admin-btn-neutral h-9 px-2.5 text-xs"
                  >
                    Clear
                  </button>
                </div>
                {settledError ? (
                  <p className="text-xs text-red-600 mt-1">{settledError}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Original {formatMoney(catalogTotal)}
                    {hasSettled ? ` · Agreed ${formatMoney(effectiveTotal)}` : ''}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Advance payment</label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    value={advance || ''}
                    onChange={(e) => {
                      const parsed = parseFloat(e.target.value);
                      setAdvance(Number.isNaN(parsed) ? 0 : parsed);
                    }}
                    min="0"
                    step="1"
                    disabled={saving}
                    className={`toolbar-input h-9 w-28 text-sm text-center px-2 focus:ring-green-500 focus:border-green-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${advanceError ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setAdvance(effectiveTotal)}
                    disabled={saving}
                    className="admin-btn admin-btn-success h-9 px-2.5 text-xs"
                  >
                    Full amount
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdvance(0)}
                    disabled={saving}
                    className="admin-btn admin-btn-neutral h-9 px-2.5 text-xs"
                  >
                    Clear
                  </button>
                </div>
                {advanceError ? (
                  <p className="text-xs text-red-600 mt-1">{advanceError}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Balance {formatMoney(balance)}
                  </p>
                )}
              </div>

              <div>
                {showColorNote ? (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-medium text-gray-600">Color note</label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowColorNote(false);
                          setColorRequest('');
                        }}
                        disabled={saving}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Remove
                      </button>
                    </div>
                    <textarea
                      value={colorRequest}
                      onChange={(e) => setColorRequest(e.target.value)}
                      disabled={saving}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={2}
                      placeholder="Special color requirements…"
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowColorNote(true)}
                    disabled={saving}
                    className="text-xs font-medium text-green-700 hover:text-green-900"
                  >
                    + Add color note
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 border-t bg-white px-4 py-3 sm:px-6 sm:py-4 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || Boolean(advanceError || settledError) || !selectedModel}
            className="admin-btn admin-btn-submit w-full py-2.5 sm:py-3 text-sm sm:text-base font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="admin-btn admin-btn-neutral w-full py-2.5 text-sm font-semibold"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default RetailOrderEditPanel;
